import styled from 'styled-components';
import { useRef, useState, useId } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { Modal, ModalTitle, ModalBody, ModalFooter, ModalFooterAlignment } from 'design-system/src/components/Modal';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Button from 'design-system/src/components/Button/Button';
import { DrawInputField, DrawingInputFieldRef } from 'design-system/src/components/DrawInputField/DrawInputField';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { client } from '../../../api';
import { createTrackingProps, trackEvent } from 'shared-utils/analytics';
import { resizeDrawnSignature } from 'shared-utils/lib/resizeDrawnSignature';

const SpacedContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export type SignFormModalProps = {
  onClose: () => void;
  signatureWidth?: number;
  signatureHeight?: number;
  analyticsSource: string;
};

/**
 * Modal with a drawing input field for getting a signature from the user.
 */
// 200 x 25 is a carefully chosen aspect ratio that allows for a signature to be inserted into many different
// pandadoc signature fields - do not change unless you have a super good reason!
export const SignFormModal = ({
  onClose,
  signatureWidth = 200,
  signatureHeight = 25,
  analyticsSource,
}: SignFormModalProps) => {
  const [step, setStep] = useState<'sign' | 'success'>('sign');
  const modalSignContentRef = useRef<HTMLDivElement>(null);
  const drawingInputFieldRef = useRef<DrawingInputFieldRef>(null);
  const [hasDrawn, setHasDrawn] = useState<boolean>(false);
  const [drawFieldHasError, setDrawFieldHasError] = useState<boolean>(false);
  const navigate = useNavigate();
  const signatureErrorId = useId();
  const { enrollmentId } = useParams();

  const submitPatientSignatureMutation = useMutation({
    mutationFn: async (resizedSigFile: File) => {
      if (!resizedSigFile) {
        throw new Error('No signature file available');
      }

      const formData = new FormData();
      formData.append('files[]', resizedSigFile);

      await client.post(`enrollments/${enrollmentId}/submit-patient-signature`, formData);
    },
    onSuccess: () => {
      setStep('success');
      trackEvent('New In-Office Enrollment: Submit Success', {
        enrollment_id: enrollmentId!,
      });
    },
    onError: () => {
      toast.custom(() => <StandardErrorToast />);
      trackEvent('New In-Office Enrollment: Submit Error', {
        enrollment_id: enrollmentId!,
      });
    },
  });

  const resizeFile = async (file: SVGSVGElement, fileName: string) => {
    return resizeDrawnSignature(file, { targetWidth: signatureWidth, targetHeight: signatureHeight, fileName });
  };

  const handleSubmit = async () => {
    if (!hasDrawn) {
      setDrawFieldHasError(true);
      return;
    }

    const svgElement = drawingInputFieldRef.current?.svg;
    if (!svgElement) return;

    let resizedFile: File;
    try {
      resizedFile = await resizeFile(svgElement, `patient-signature-${enrollmentId}-${Date.now()}-original-size.png`);
      submitPatientSignatureMutation.mutate(resizedFile);
    } catch (error) {
      console.error('Error resizing file:', error);
      Sentry.captureException(error, {
        extra: {
          enrollmentId,
        },
      });
      toast.custom(() => <StandardErrorToast />);
      return;
    }
  };

  const signStep = (
    <>
      <ModalTitle>Add patient signature</ModalTitle>

      <ModalBody ref={modalSignContentRef}>
        <Typography styledAs="bodyLargeDMSans">After signing, this form will be locked to ensure accuracy.</Typography>
        <div>
          <DrawInputField
            label="Signature"
            hideLabel
            drawingInputFieldRef={drawingInputFieldRef}
            onDrawingChange={(data) => {
              if (data.length > 0) {
                setHasDrawn(true);
                setDrawFieldHasError(false);
              }
            }}
            onClearDrawing={() => setHasDrawn(false)}
            hasError={drawFieldHasError}
            aria-describedby={signatureErrorId}
            aspectRatio={`${signatureWidth} / ${signatureHeight}`}
          />
          <ErrorMessage id={signatureErrorId} visible={drawFieldHasError} textStyledAs="bodyExtraSmallSpaceGrotesk">
            Signature required
          </ErrorMessage>
        </div>

        <Typography
          styledAs="bodyExtraSmallDMSans"
          color="var(--grayscale-600)"
          {...createTrackingProps({ unmask: true })}
        >
          By electronically signing this document, I agree that my signature and initials are the equivalent of my
          handwritten signature and are considered originals on all documents, including legally binding contracts.
        </Typography>
      </ModalBody>

      <ModalFooter align={ModalFooterAlignment.SpaceBetween}>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={submitPatientSignatureMutation.isPending}
          {...createTrackingProps({ elementName: `${analyticsSource}: Cancel Patient Signature Button` })}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          isLoading={submitPatientSignatureMutation.isPending}
          {...createTrackingProps({ elementName: `${analyticsSource}: Submit Patient Signature Button` })}
        >
          Submit patient signature
        </Button>
      </ModalFooter>
    </>
  );

  const successStep = (
    <>
      <ModalTitle>Thanks for signing!</ModalTitle>
      <ModalBody {...createTrackingProps({ unmask: true })}>
        <SpacedContent>
          <Typography styledAs="bodyLargeDMSans">
            This form is now pending. We'll complete the remaining sections using the information from the prescription
            and notify you when the form is ready to be reviewed for provider signature.
          </Typography>
          <Typography styledAs="bodyLargeDMSans">
            You can review this and other pending forms at the top of the Prescriptions page.
          </Typography>
        </SpacedContent>
      </ModalBody>
      <ModalFooter>
        <Button to="/prescriptions">Return to Prescriptions</Button>
        <Button variant="secondary" onClick={() => location.reload()}>
          View pending form
        </Button>
      </ModalFooter>
    </>
  );

  return (
    <Modal onClose={step === 'sign' ? onClose : () => navigate('/prescriptions')} maxWidth="800px">
      {step === 'sign' ? signStep : successStep}
    </Modal>
  );
};

export default SignFormModal;
